import { Link } from "react-router";
import { BloomLogo } from "@bloom/ui";
import { Facebook, Instagram, Twitter } from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";

// User-facing payment methods. Stripe is the processor (not a public badge);
// Omise from the mockup is intentionally omitted — Bloom v1 uses Stripe only.
const PAYMENT_BADGES = [
  // TODO(client-asset): swap to official brand SVGs once licensed
  { src: "/payment-methods/visa.svg", alt: "Visa" },
  { src: "/payment-methods/mastercard.svg", alt: "Mastercard" },
  { src: "/payment-methods/promptpay.svg", alt: "PromptPay" },
];

function TikTokIcon({ className }: { className?: string }) {
  // Lucide doesn't ship a TikTok glyph — inline the brand mark.
  // fill="currentColor" so the icon inherits the parent's text color.
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.001.595.04.88.12V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.95a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
    </svg>
  );
}

// TODO(client-asset): replace handles + URLs when client confirms socials
const SOCIAL_LINKS = [
  { Icon: Facebook, label: "Facebook", href: "https://facebook.com/bloom.tv" },
  { Icon: Instagram, label: "Instagram", href: "https://instagram.com/bloom.tv" },
  { Icon: Twitter, label: "X", href: "https://x.com/bloom_tv" },
  { Icon: TikTokIcon, label: "TikTok", href: "https://tiktok.com/@bloom.tv" },
] as const;

export function Footer() {
  const { t } = useLocale();
  const { user } = useAuth();
  const year = new Date().getFullYear();

  // OTT pattern (Netflix / Disney+ / Hulu): the footer's "Account" column
  // swaps based on signed-in state. Logged-out viewers see Log in / Sign up.
  // Logged-in viewers see the auth-only links (wallet, purchase history,
  // account settings). Either way, the column has the same heading and slot
  // count so the grid stays balanced.
  const accountLinks = user
    ? [
        { to: "/account", label: t("nav.account") },
        { to: "/account/wallet", label: t("account.tab.wallet") },
        { to: "/account/purchases", label: t("footer.purchase_history") },
      ]
    : [
        { to: "/login", label: t("nav.login") },
        { to: "/signup", label: t("nav.signup") },
      ];

  return (
    <footer className="bg-[var(--brand-ink)] text-white">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-12">
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-3 lg:grid-cols-7">
          {/* Column 1 — Logo + tagline (wider on lg) */}
          <div className="col-span-2 lg:col-span-2">
            {/* BloomLogo uses currentColor — passing text-[--brand-rose]
                gives the wordmark the bright-pink pop the mockup shows
                against the dark footer ink. */}
            <BloomLogo className="h-7 text-[var(--brand-rose)]" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/60">
              {t("footer.tagline")}
            </p>
          </div>

          <FooterCol
            heading={t("footer.menu_heading")}
            links={[
              { to: "/", label: t("nav.home") },
              { to: "/series", label: t("nav.all_series") },
              { to: "/series?sort=newest", label: t("nav.new") },
              { to: "/about", label: t("nav.about") },
            ]}
          />

          <FooterCol
            heading={t("footer.help_heading")}
            links={[
              { to: "/faq", label: t("footer.faq") },
              { to: "/contact-us", label: t("footer.contact_us") },
              { to: "/report-a-problem", label: t("footer.report_problem") },
            ]}
          />

          <FooterCol heading={t("footer.account_heading")} links={accountLinks} />

          <FooterCol
            heading={t("footer.legal_heading")}
            links={[
              { to: "/terms", label: t("footer.terms_of_service") },
              { to: "/privacy", label: t("footer.privacy") },
              { to: "/cookies", label: t("footer.cookies") },
              { to: "/refund-policy", label: t("footer.refund_policy") },
            ]}
          />

          {/* Follow us — bare icons in a horizontal row, no circles */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <p className="mb-4 text-sm font-bold uppercase tracking-wider text-white">
              {t("footer.follow_heading")}
            </p>
            <ul className="flex items-center gap-4">
              {SOCIAL_LINKS.map(({ Icon, label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="inline-flex text-white transition-opacity hover:opacity-70"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip — no divider; just generous mt for breathing room */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-xs text-white/60">{t("footer.copyright", { year })}</p>
          <ul className="flex items-center gap-3">
            {PAYMENT_BADGES.map((b) => (
              <li key={b.src}>
                <img src={b.src} alt={b.alt} className="h-5 w-auto" loading="lazy" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}

interface FooterColProps {
  heading: string;
  links: { to: string; label: string }[];
}

function FooterCol({ heading, links }: FooterColProps) {
  return (
    <div>
      <p className="mb-4 text-sm font-bold uppercase tracking-wider text-white">{heading}</p>
      <ul className="space-y-1">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="block py-1 text-sm text-white/90 transition-colors hover:text-[var(--brand-rose)]"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
